import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CustomBackground } from '@td/shared';
import { CLIENT_ID, del, get, patch } from '../lib/api';

const KEY = ['backgrounds'];

export function useCustomBackgrounds() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => get<CustomBackground[]>('/backgrounds'),
  });
}

export function useUploadBackground() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ file, name }: { file: File; name?: string }) => {
      const body = new FormData();
      body.append('image', file);
      if (name) body.append('name', name);

      // Not the shared JSON helper: a multipart body must not carry a
      // Content-Type header of our own, or the boundary is lost.
      const res = await fetch('/api/backgrounds', {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-Client-Id': CLIENT_ID },
        body,
      });

      if (!res.ok) {
        const message = await res
          .json()
          .then((b: { error?: string }) => b.error)
          .catch(() => null);
        throw new Error(message ?? "That image couldn't be uploaded");
      }

      return (await res.json()) as CustomBackground;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useRenameBackground() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      patch<CustomBackground>(`/backgrounds/${id}`, { name }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteBackground() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del(`/backgrounds/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      // Deleting the one in use clears the setting server-side, so the
      // current user has to be refetched for the page to stop asking for it.
      void qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
}
